import { useState, useEffect } from 'react'
import {
  Box, Card, CardContent, Typography, Chip, Button, IconButton,
  TextField, Grid, Divider, Stack, Collapse,
} from '@mui/material'
import { Edit, Add, Save, Close, Factory } from '@mui/icons-material'

// Fallback demo data used when API returns empty
const DEMO_PLANTS = [
  {
    code: '1002',
    name: 'FBO',
    city: 'Faribault, MN',
    lines: [
      {
        id: 'L1',
        name: 'Line 1 — Cans (Large)',
        description: '#10 cans, institutional sizes',
        operatingDays: 5,
        hoursPerDay: 8,
        changeoverHrs: 2.0,
        products: ['#10 Chili Beans', '#10 Kidney Beans', '#10 Pinto Beans', '#10 Great Northern'],
      },
      {
        id: 'L2',
        name: 'Line 2 — Cans (Small)',
        description: '#300 and #303 cans, retail sizes',
        operatingDays: 5,
        hoursPerDay: 8,
        changeoverHrs: 1.5,
        products: ['15oz Chili Beans', '15oz Kidney Beans', '15oz Pinto Beans', '15oz Black Beans'],
      },
      {
        id: 'L3',
        name: 'Line 3 — Pouches',
        description: 'Flexible pouch packaging',
        operatingDays: 5,
        hoursPerDay: 8,
        changeoverHrs: 1.0,
        products: ['Pouch Refried Beans', 'Pouch Chili'],
      },
      {
        id: 'L4',
        name: 'Line 4 — Dry Pack',
        description: 'Dry bean bagging and boxing',
        operatingDays: 5,
        hoursPerDay: 4,
        changeoverHrs: 0.5,
        products: ['1lb Pinto Dry', '1lb Black Dry', '2lb Navy Dry'],
      },
    ],
  },
  {
    code: '2001',
    name: 'Tucson',
    city: 'Tucson, AZ',
    lines: [
      {
        id: 'T1',
        name: 'Line 1 — Salsa',
        description: 'Jarred salsa and sauces',
        operatingDays: 5,
        hoursPerDay: 8,
        changeoverHrs: 1.5,
        products: ['16oz Mild Salsa', '16oz Medium Salsa', '16oz Hot Salsa'],
      },
    ],
  },
]

export default function PlantConfig({ plants: plantsProp }) {
  const [plants, setPlants] = useState(() =>
    Array.isArray(plantsProp) && plantsProp.length > 0 ? plantsProp : DEMO_PLANTS,
  )
  const [editingLine, setEditingLine] = useState(null)
  const [editValues, setEditValues] = useState({})

  useEffect(() => {
    if (Array.isArray(plantsProp) && plantsProp.length > 0) {
      setPlants(plantsProp)
    }
  }, [plantsProp])

  const startEdit = (plantCode, line) => {
    setEditingLine(`${plantCode}-${line.id}`)
    setEditValues({ ...line })
  }

  const cancelEdit = () => {
    setEditingLine(null)
    setEditValues({})
  }

  const saveEdit = (plantCode) => {
    setPlants((prev) =>
      prev.map((p) =>
        p.code === plantCode
          ? { ...p, lines: p.lines.map((l) => (l.id === editValues.id ? { ...editValues } : l)) }
          : p,
      ),
    )
    setEditingLine(null)
    setEditValues({})
  }

  const addLine = (plantCode) => {
    const plant = plants.find((p) => p.code === plantCode)
    const nextId = `L${plant.lines.length + 1}`
    const newLine = {
      id: nextId,
      name: `Line ${plant.lines.length + 1}`,
      description: '',
      operatingDays: 5,
      hoursPerDay: 8,
      changeoverHrs: 1.0,
      products: [],
    }
    setPlants((prev) =>
      prev.map((p) => (p.code === plantCode ? { ...p, lines: [...p.lines, newLine] } : p)),
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {plants.map((plant) => (
        <Card key={plant.code} variant="outlined">
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Factory color="primary" />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5">{plant.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Code: {plant.code} &middot; {plant.city}
                </Typography>
              </Box>
              <Chip label={`${plant.lines.length} lines`} size="small" color="primary" variant="outlined" />
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Stack spacing={2}>
              {plant.lines.map((line) => {
                const isEditing = editingLine === `${plant.code}-${line.id}`
                return (
                  <Card key={line.id} variant="outlined" sx={{ bgcolor: 'surface.containerLow' }}>
                    <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1">{line.name}</Typography>
                          <Typography variant="body2" color="text.secondary">{line.description}</Typography>
                        </Box>
                        {!isEditing && (
                          <IconButton size="small" onClick={() => startEdit(plant.code, line)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        )}
                      </Box>

                      <Collapse in={isEditing}>
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                              label="Name"
                              size="small"
                              fullWidth
                              value={editValues.name || ''}
                              onChange={(e) => setEditValues((v) => ({ ...v, name: e.target.value }))}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                              label="Description"
                              size="small"
                              fullWidth
                              value={editValues.description || ''}
                              onChange={(e) => setEditValues((v) => ({ ...v, description: e.target.value }))}
                            />
                          </Grid>
                          <Grid size={{ xs: 4 }}>
                            <TextField
                              label="Operating Days/Wk"
                              size="small"
                              type="number"
                              fullWidth
                              value={editValues.operatingDays ?? ''}
                              onChange={(e) => setEditValues((v) => ({ ...v, operatingDays: +e.target.value }))}
                            />
                          </Grid>
                          <Grid size={{ xs: 4 }}>
                            <TextField
                              label="Hours/Day"
                              size="small"
                              type="number"
                              fullWidth
                              value={editValues.hoursPerDay ?? ''}
                              onChange={(e) => setEditValues((v) => ({ ...v, hoursPerDay: +e.target.value }))}
                            />
                          </Grid>
                          <Grid size={{ xs: 4 }}>
                            <TextField
                              label="Changeover Hrs"
                              size="small"
                              type="number"
                              fullWidth
                              value={editValues.changeoverHrs ?? ''}
                              onChange={(e) => setEditValues((v) => ({ ...v, changeoverHrs: +e.target.value }))}
                            />
                          </Grid>
                          <Grid size={{ xs: 12 }}>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                              <Button size="small" startIcon={<Close />} onClick={cancelEdit}>Cancel</Button>
                              <Button size="small" variant="contained" startIcon={<Save />} onClick={() => saveEdit(plant.code)}>
                                Save
                              </Button>
                            </Box>
                          </Grid>
                        </Grid>
                      </Collapse>

                      {!isEditing && (
                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                          <Grid size={{ xs: 4 }}>
                            <Typography variant="caption" color="text.secondary">Operating Days</Typography>
                            <Typography variant="body2">{line.operatingDays} days/wk</Typography>
                          </Grid>
                          <Grid size={{ xs: 4 }}>
                            <Typography variant="caption" color="text.secondary">Hours/Day</Typography>
                            <Typography variant="body2">{line.hoursPerDay} hrs</Typography>
                          </Grid>
                          <Grid size={{ xs: 4 }}>
                            <Typography variant="caption" color="text.secondary">Changeover</Typography>
                            <Typography variant="body2">{line.changeoverHrs} hrs</Typography>
                          </Grid>
                          <Grid size={{ xs: 12 }}>
                            <Typography variant="caption" color="text.secondary">Products</Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                              {line.products.map((p) => (
                                <Chip key={p} label={p} size="small" variant="outlined" />
                              ))}
                            </Box>
                          </Grid>
                        </Grid>
                      )}
                    </CardContent>
                  </Card>
                )
              })}
            </Stack>

            <Button
              startIcon={<Add />}
              sx={{ mt: 2 }}
              onClick={() => addLine(plant.code)}
            >
              Add Line
            </Button>
          </CardContent>
        </Card>
      ))}
    </Box>
  )
}
